import React, { useState, useEffect } from 'react';
import { BuyNewHomeCalculationResults } from '../types';
import { InfoTooltip } from './InfoTooltip';

interface ResultsDisplayProps {
  results: BuyNewHomeCalculationResults | null;
  isLoading: boolean;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumSignificantDigits: 3,
  }).format(value);
};

export const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ results, isLoading }) => {
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    if (results && !isLoading) {
        const { summary, monthlyData } = results;
        const loanTerm = Math.ceil(monthlyData.length / 12);
        const isFinancingBetter = summary.financeScenarioFinalNetWorth > summary.payCashScenarioFinalNetWorth;
        const difference = Math.abs(summary.financeScenarioFinalNetWorth - summary.payCashScenarioFinalNetWorth);

        const higherStrategy = isFinancingBetter ? 'Financing and Investing' : 'Paying Cash';
        const formattedDiff = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(difference);

        setAnnouncement(`Calculation complete. ${higherStrategy} results in a higher projected net worth by ${formattedDiff} after ${loanTerm} years.`);
    } else {
        setAnnouncement('');
    }
  }, [results, isLoading]);

  if (isLoading) {
    return (
      <div className="text-center p-8">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-bp-dark-blue dark:border-bp-light-blue mx-auto"></div>
        <p className="mt-4 text-lg font-semibold text-gray-700 dark:text-gray-300">Calculating your financial future...</p>
      </div>
    );
  }

  if (!results) {
    return null;
  }

  const { summary, monthlyData } = results;
  const loanTerm = Math.ceil(monthlyData.length / 12);
  const isFinancingBetter = summary.financeScenarioFinalNetWorth > summary.payCashScenarioFinalNetWorth;
  const difference = Math.abs(summary.financeScenarioFinalNetWorth - summary.payCashScenarioFinalNetWorth);
  const lastMonth = monthlyData[monthlyData.length - 1];

  return (
    <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-slate-700 transition-colors">
      <div className="sr-only" aria-live="polite" aria-atomic="true">{announcement}</div>
      <h2 className="text-2xl font-bold text-bp-slate dark:text-white mb-4 border-b dark:border-slate-600 pb-2">Results: Finance vs. Pay Cash</h2>
      
      <div className={`p-4 rounded-lg mb-6 ${isFinancingBetter ? 'bg-blue-100 dark:bg-blue-900/40 border-blue-400 dark:border-blue-600 text-blue-900 dark:text-blue-100' : 'bg-green-100 dark:bg-green-900/40 border-green-400 dark:border-green-600 text-green-900 dark:text-green-100'} border-l-4`}>
        <h3 className="text-xl font-bold">Highest Projected Net Worth</h3>
        <p className="mt-1">
          Based on your inputs, {isFinancingBetter ? 'financing your home' : 'paying in cash'} is projected to result in a higher net worth by{' '}
          <strong className="text-lg">{formatCurrency(difference)}</strong> after {loanTerm} years.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-lg">
          <h4 className="font-bold text-lg text-bp-dark-blue dark:text-bp-light-blue mb-2">Strategy 1: Finance & Invest</h4>
          <ul className="space-y-3 text-gray-700 dark:text-gray-300">
            <li><strong>Final Home Equity:</strong> {formatCurrency(lastMonth.homeEquity)}</li>
            <li>
                <InfoTooltip text="The final value of your investment portfolio, which starts with your total cash minus the down payment and closing costs.">
                    <strong>Final Investment Value:</strong>
                </InfoTooltip>
                <span className="font-bold ml-1">{formatCurrency(lastMonth.investedBalanceFinance)}</span>
            </li>
             <li>
                <InfoTooltip text="Your projected net worth from this strategy, calculated as: Final Home Equity + Final Investment Value.">
                    <strong>Final Net Worth:</strong>
                </InfoTooltip>
                <span className="font-bold ml-1">{formatCurrency(summary.financeScenarioFinalNetWorth)}</span>
            </li>
          </ul>
        </div>

        <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-lg">
          <h4 className="font-bold text-lg text-green-700 dark:text-green-400 mb-2">Strategy 2: Pay Cash</h4>
          <ul className="space-y-3 text-gray-700 dark:text-gray-300">
             <li><strong>Final Home Value:</strong> {formatCurrency(lastMonth.homeValue)}</li>
             <li>
                <InfoTooltip text="The model assumes you invest what would have been your monthly mortgage payment (plus any insurance savings) at regular intervals, earning returns based on the investment assumptions provided.">
                    <strong>Final Investment Value:</strong>
                </InfoTooltip>
                <span className="font-bold ml-1">{formatCurrency(lastMonth.investedBalancePayCash)}</span>
            </li>
             <li>
                <InfoTooltip text="Your projected net worth from this strategy, calculated as: Final Home Value + Final Investment Value.">
                    <strong>Final Net Worth:</strong>
                </InfoTooltip>
                <span className="font-bold ml-1">{formatCurrency(summary.payCashScenarioFinalNetWorth)}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};